import { createElement as h, Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import type { CanvasView } from './canvas-view.js';
import type { DesignCanvasController } from './design-canvas/controller.js';
import { LandingPage, type LandingPageProps } from './ui/landing/landing-page.js';
import { TooltipProvider } from './ui/tooltip.js';

export interface CanvasHandle { controller: DesignCanvasController; dispose(): void }

/**
 * One React root for the app content: the home page and the project canvas render into it in turn,
 * so they share React state and portals instead of each page mounting and tearing down its own root.
 * The host moves between the page mount and the canvas workspace; it lays out as
 * `display: contents`, so whichever parent it sits in sizes the rendered view.
 */
export class AppRoot {
  readonly host = document.createElement('div');
  private readonly root;
  private view = 0;

  constructor() {
    this.host.className = 'app-root';
    this.root = createRoot(this.host);
  }

  showLanding(parent: HTMLElement, props: LandingPageProps): void {
    this.view++;
    parent.replaceChildren(this.host);
    this.root.render(h(TooltipProvider, { children: h(LandingPage, props) }));
  }

  /** Shows a canvas view; disposing it clears the root only while the canvas is still the view shown. */
  showCanvas(parent: HTMLElement, canvas: CanvasView): CanvasHandle {
    const view = ++this.view;
    parent.replaceChildren(this.host);
    this.root.render(h(Fragment, { key: `canvas-${view}` }, canvas.element));
    return {
      controller: canvas.controller,
      dispose: () => {
        canvas.controller.dispose();
        if (this.view === view) { this.view++; this.root.render(null); }
      },
    };
  }
}
